/**
 * Dexie database for evse3 (schema v4).
 *
 * Mirrors data/storage-schema.yaml — that file is the source of truth for the
 * index specs. Row interfaces are typed per repository contract so the
 * compiler keeps Dexie usage honest.
 *
 * v1 → v2 (Session 8f.4, 2026-05-25): + localAuthList + localAuthListVersions
 * for SPEC-001 OCPP 1.6J LocalAuthListManagement.
 * v2 → v3 (Session 8f.9, 2026-05-25): + vehicleProfiles + connectorState
 * for FEAT-010..015 vehicle profile library + per-connector binding.
 * v3 → v4 (Session 19b, 2026-05-28): + variableValues for the OCPP 2.0.1
 * Device Model (v2.0.1 stations store config here instead of the flat
 * `configurations` table; v1.6 stations are untouched).
 * v4 → v5 (Session 19d, 2026-05-29): + displayMessages for the OCPP 2.0.1
 * Display Messages feature (v2.0.1-only).
 * v5 → v6 (2026-05-29, TG-B): + transactionSamples — persisted per-transaction
 * progress series (power/SoC/energy over time) for the transaction-history graph.
 * All upgrades are non-destructive (additive only).
 */
import Dexie, { type Table } from 'dexie';
import type {
  ChargingProfileSnapshot,
  ConfigurationKeySnapshot,
  DisplayMessageSnapshot,
  OfflineSessionSnapshot,
  TopologySnapshot,
  TransactionSnapshot,
} from '@/domain';
import type { AuthStatus, StationId, EvseId, VehicleProfileId } from '@/domain/types';
import type { SnapshotKind } from '@/persistence/ports';

export const DB_NAME = 'evse3';
export const DB_VERSION = 6;

export interface StationRow {
  id: StationId;
  cpid: string;
  wsUrl: string;
  displayName?: string;
  displayChecksum: string;
  ocppVersion: string;
  createdAt: number;
  lastConnectedAt?: number;
}

export interface TopologyRow extends TopologySnapshot {
  stationId: StationId;
}

export interface ConfigurationRow extends ConfigurationKeySnapshot {
  stationId: StationId;
}

export interface ChargingProfileRow extends ChargingProfileSnapshot {
  stationId: StationId;
}

export interface TransactionRow extends TransactionSnapshot {
  stationId: StationId;
}

export interface MeterRow {
  stationId: StationId;
  evseId: EvseId;
  sharedMeterWh: number;
  updatedAt: number;
}

export interface OfflineQueueRow extends OfflineSessionSnapshot {
  stationId: StationId;
  failureReason?: string;
}

/**
 * Persisted shape of one OCPP frame in the event log.
 *
 * Session 13 / CR mimo-rozsahu #10 — `direction` uses the wire-parlance
 * `'SEND' | 'RECV'` labels for storage clarity ("send" = CP→CSMS,
 * "recv" = CSMS→CP). The in-memory `EventLogEntry` port type at
 * `persistence/ports.ts` uses the React-friendly `'in' | 'out'` form,
 * and `EventLogRepositoryDexie` translates between them at the seam.
 * This dichotomy is intentional, NOT a type mismatch — keep the
 * translation in the repo if you touch either type.
 */
export interface EventLogRow {
  stationId: StationId;
  seq: number;
  createdAt: number;
  direction: 'SEND' | 'RECV';
  messageType: 'CALL' | 'CALLRESULT' | 'CALLERROR';
  messageId: string;
  action?: string;
  payload: unknown;
}

export interface TabLockRow {
  cpid: string;
  wsUrl: string;
  tabId: string;
  heldSince: number;
  lastHeartbeat: number;
  ocppConnected: boolean;
  browserUserAgent?: string;
}

export interface LocalAuthListRow {
  stationId: StationId;
  idTag: string;
  status: AuthStatus;
  expiryDate?: string;
  parentIdTag?: string;
  updatedAt: number;
}

export interface LocalAuthListVersionRow {
  stationId: StationId;
  listVersion: number;
  updatedAt: number;
}

/** FEAT-010 — workspace-wide vehicle profile library (Session 8f.9). */
export interface VehicleProfileRow {
  id: VehicleProfileId;
  name: string;
  batteryKwh: number;
  maxAcPowerKw: number;
  maxDcPowerKw: number;
  defaultStartSocPct: number;
  /**
   * Optional DC charging curve (max accepted kW vs SoC). Not indexed, so this
   * needs no Dexie version bump — existing rows simply lack the field and are
   * treated as a flat `maxDcPowerKw` cap.
   */
  chargingCurve?: { socPct: number; powerKw: number }[];
  isPreset: boolean;
  createdAt: number;
}

/** FEAT-011 — per-connector persisted state (vehicle binding + SoC). */
export interface ConnectorStateRow {
  stationId: StationId;
  connectorId: number;
  assignedVehicleId?: VehicleProfileId;
  currentSocPct?: number;
  updatedAt: number;
}

/**
 * Session 19b — OCPP 2.0.1 Device Model row. v2.0.1 stations store their
 * configuration here (structured) instead of the flat `configurations`
 * table. `evseId` / `connectorId` use 0 = "not qualified". Composite key
 * is `[stationId+component+evseId+connectorId+variable+attributeType]`.
 */
export interface VariableValueRow {
  stationId: StationId;
  component: string;
  evseId: number;
  connectorId: number;
  variable: string;
  attributeType: string;
  value: string;
  mutability: string;
  dataType: string;
  unit?: string;
  section: string;
}

/** Session 19d — OCPP 2.0.1 Display Messages (v2.0.1-only). */
export interface DisplayMessageRow extends DisplayMessageSnapshot {
  stationId: StationId;
}

export interface SnapshotRow {
  id: string;
  /** Optional for workspace-level (cross-station) snapshots. */
  stationId?: StationId;
  kind: SnapshotKind;
  direction: 'out' | 'in';
  createdAt: number;
  schemaVersion: number;
  payload: unknown;
  summary?: {
    configKeyCount: number;
    profileCount: number;
    offlineQueueCount: number;
  };
}

/**
 * TG-B — persisted per-transaction progress series. One row per (station, tx)
 * holding the bounded telemetry buffer (≤ MAX_SAMPLES points) captured during
 * charging. Loaded only when the user drills into a transaction in History.
 */
export interface TransactionSampleRow {
  stationId: StationId;
  transactionId: number;
  samples: { t: number; powerW: number; socPct: number | null; energyWh: number }[];
}

export class Evse3Database extends Dexie {
  stations!: Table<StationRow, StationId>;
  topologies!: Table<TopologyRow, StationId>;
  configurations!: Table<ConfigurationRow, [StationId, string]>;
  chargingProfiles!: Table<ChargingProfileRow, [StationId, number]>;
  transactions!: Table<TransactionRow, [StationId, number]>;
  meters!: Table<MeterRow, [StationId, EvseId]>;
  offlineQueue!: Table<OfflineQueueRow, [StationId, string]>;
  eventLog!: Table<EventLogRow, [StationId, number]>;
  tabLocks!: Table<TabLockRow, [string, string]>;
  snapshots!: Table<SnapshotRow, string>;
  localAuthList!: Table<LocalAuthListRow, [StationId, string]>;
  localAuthListVersions!: Table<LocalAuthListVersionRow, StationId>;
  vehicleProfiles!: Table<VehicleProfileRow, VehicleProfileId>;
  connectorState!: Table<ConnectorStateRow, [StationId, number]>;
  variableValues!: Table<VariableValueRow, [StationId, string, number, number, string, string]>;
  displayMessages!: Table<DisplayMessageRow, [StationId, number]>;
  transactionSamples!: Table<TransactionSampleRow, [StationId, number]>;

  constructor(name = DB_NAME) {
    super(name);
    // v1 — original ten-table schema.
    this.version(1).stores({
      stations: '&id, &[cpid+wsUrl], cpid, displayName, ocppVersion, lastConnectedAt',
      topologies: '&stationId',
      configurations: '[stationId+key], stationId',
      chargingProfiles: '[stationId+id], stationId, [stationId+purpose+stackLevel+connectorId]',
      transactions:
        '[stationId+id], stationId, [stationId+connectorId], [stationId+stoppedAt], [stationId+recoveryState]',
      meters: '[stationId+evseId]',
      offlineQueue: '[stationId+queueId], stationId, [stationId+status]',
      eventLog: '[stationId+seq], stationId, [stationId+action], [stationId+createdAt]',
      tabLocks: '&[cpid+wsUrl], tabId, lastHeartbeat',
      snapshots: 'id, stationId, createdAt, kind',
    });
    // v2 — SPEC-001 LocalAuthList (Session 8f.4).
    this.version(2).stores({
      localAuthList: '[stationId+idTag], stationId, status',
      localAuthListVersions: '&stationId',
    });
    // v3 — FEAT-010..015 Vehicle profiles (Session 8f.9).
    this.version(3).stores({
      vehicleProfiles: '&id, name',
      connectorState: '[stationId+connectorId], stationId',
    });
    // v4 — Session 19b Device Model (OCPP 2.0.1 Variables/Components).
    this.version(4).stores({
      variableValues:
        '[stationId+component+evseId+connectorId+variable+attributeType], stationId',
    });
    // v5 — Session 19d Display Messages (OCPP 2.0.1).
    this.version(5).stores({
      displayMessages: '[stationId+id], stationId',
    });
    // v6 — TG-B per-transaction progress series.
    this.version(6).stores({
      transactionSamples: '[stationId+transactionId], stationId',
    });
  }
}

/** Module-level singleton — one open Dexie per tab. */
export const db = new Evse3Database();
